import { useState, useEffect } from 'react';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface PrerequisiteStatus {
  all_complete: boolean;
  has_resume: boolean;
  has_brag_sheet: boolean;
  has_dream_sheet: boolean;
  has_career_report: boolean;
  resume_quality_score: number;
  brag_items_count: number;
  dream_items_count: number;
  missing_items: string[];
  message: string;
  next_steps: string[];
}

export interface UseJobMatchPrerequisitesReturn {
  prerequisites: PrerequisiteStatus | null;
  isLoading: boolean;
  error: string | null;
  checkPrerequisites: () => Promise<void>;
  canAccessJobMatch: boolean;
}

/**
 * Hook to check and manage Job Match AI prerequisites
 *
 * Validates that user has:
 * - Uploaded resume with extracted content
 * - Completed BRAG sheet (minimum 5 items)
 * - Completed DREAM sheet (minimum 5 items)
 * - Generated Career Intelligence Report
 */
export function useJobMatchPrerequisites(): UseJobMatchPrerequisitesReturn {
  const { user } = useAuth();
  const [prerequisites, setPrerequisites] = useState<PrerequisiteStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkPrerequisites = async () => {
    if (!user) {
      setError('User not authenticated');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase
        .rpc('get_prerequisite_status', { p_user_id: user.id });

      if (rpcError) {
        throw rpcError;
      }

      setPrerequisites(data as PrerequisiteStatus);
    } catch (err: any) {
      console.error('Error checking prerequisites:', err);
      setError(err.message || 'Failed to check prerequisites');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkPrerequisites();
  }, [user?.id]);

  return {
    prerequisites,
    isLoading,
    error,
    checkPrerequisites,
    canAccessJobMatch: prerequisites?.all_complete || false,
  };
}
